import { useCallback, useEffect, useRef } from 'react';
import { Bool_Enum } from '@weave/schema-gen-ts/dist/shared/null/types.pb';
import { isEqual } from 'lodash-es';
import { useQuery } from 'react-query';
import { LocationsApi } from '@frontend/api-locations';
import { http } from '@frontend/fetch';
import { LocationPickerProvider, useLocationPicker } from '@frontend/location-picker';
import { usePanelStore } from '@frontend/panel-engine';
import {
  useAllAccessScopeStore,
  useAppScopeCatalog,
  useAppScopeNaming,
  useAppScopeSelection,
  useAppScopeSetters,
} from '@frontend/scope';
import { useContactPanelStore } from '@frontend/shared';
import { useUserContextStore } from '@frontend/shell-utils';
import { useSlidePanelStore } from '@frontend/slide-panel';
import { useWorkspaceShallowStore } from '@frontend/workspaces';
import { useAlert } from '@frontend/design-system';
import { useApplyLocationSelectionSideEffects } from '../../../utils/configure-location';
import { useProcessScopeDataFromOrgIds } from '../../scope-init';
import { DataLocationSearchResults } from '../types';
import {
  PrivilegedLocationProvider,
  PrivilegedLocationPicker as PrivilegedLocationPickerNew,
} from './privileged-multi-location-selector-content';
import { usePrivilegedPhoneSoftphoneIntegration } from './use-privileged-phone-softphone-integration';

const useAppendScopeData = ({ orgIds }: { orgIds: string[] }) => {
  const lastOrgIds = useRef<string[]>([]);
  const { appendAccessibleGroupData, appendAccessibleLocationData, appendAccessibleOrgData, appendScopeHierarchy } =
    useAppScopeSetters();
  const setAllAccessLocationMap = useAllAccessScopeStore((state) => state.setAccessibleLocationMap);

  const { isSuccess, locationMap, groupMap, orgMap, locationHierarchy } = useProcessScopeDataFromOrgIds({
    orgIds: orgIds,
  });

  useEffect(() => {
    if (isSuccess && !isEqual(lastOrgIds.current, orgIds)) {
      const scopeHierarchy = Object.entries(locationHierarchy).reduce((acc, [groupId, locations]) => {
        const group = groupMap[groupId];

        if (!group.orgId) return acc;

        if (acc[group.orgId]) {
          acc[group.orgId][group.id] = locations;
        } else {
          acc[group.orgId] = {
            [group.id]: locations,
          };
        }

        return acc;
      }, {} as Record<string, Record<string, string[]>>);

      appendAccessibleGroupData(groupMap);
      appendAccessibleLocationData(locationMap);
      setAllAccessLocationMap(locationMap);
      appendAccessibleOrgData(orgMap);
      appendScopeHierarchy(scopeHierarchy);

      lastOrgIds.current = orgIds;
    }
  }, [
    isSuccess,
    orgIds,
    appendAccessibleGroupData,
    appendAccessibleLocationData,
    appendAccessibleOrgData,
    appendScopeHierarchy,
    groupMap,
    locationHierarchy,
    locationMap,
    orgMap,
  ]);
};

const LoadPrivilegedLocations = () => {
  const { debouncedSearchTerm } = useLocationPicker(['debouncedSearchTerm']);

  const { data: locationResults = [], isLoading: _ } = useQuery(
    ['locations', debouncedSearchTerm],
    async ({ queryKey }) => {
      const [, query] = queryKey;
      const res = await LocationsApi.getPrivilegedLocations(query);

      const dataTransform = res?.locations?.map((location) => ({
        ...location,
        locationID: location.locationId,
        parentID: location.parentId,
      })) as DataLocationSearchResults[];
      return dataTransform;
    },
    {
      enabled: !!debouncedSearchTerm,
      select: (data) => {
        return data.filter((location) => location.active === Bool_Enum.TRUE);
      },
    }
  );

  useAppendScopeData({ orgIds: [...new Set(locationResults.map((location) => location.orgId).filter(Boolean))] });

  return null;
};

const LocationPickerProviderPrivileged = ({
  children,
  closeModal,
}: {
  children: React.ReactNode;
  closeModal?: () => void;
}) => {
  const alerts = useAlert();
  const { selectedGroupId, selectedLocationIds, selectedOrgId, selectedScope } = useAppScopeSelection();
  const { getScopeName } = useAppScopeNaming();
  const {
    accessibleScopeHierarchy: scopeHierarchy,
    accessibleLocationData,
    accessibleGroupData,
  } = useAppScopeCatalog();
  const { setSelectedLocationIds, setSelectedOrgId, setSelectedScope } = useAppScopeSetters();
  const { pendingWorkspaceId } = useWorkspaceShallowStore('pendingWorkspaceId');
  const { inFlightLocationIds, inFlightOrganizationId } = useLocationPicker([
    'inFlightLocationIds',
    'inFlightOrganizationId',
  ]);
  const { applyLocationSelectionSideEffects } = useApplyLocationSelectionSideEffects();

  const handleOrganizationChange = useCallback(() => {
    // Trust the UI state - if pendingWorkspaceId is set, user selected a workspace
    usePanelStore.getState().closeAllPanels();
    http.setLocationIdHeader(inFlightLocationIds[0]);

    setSelectedLocationIds(inFlightLocationIds, { skipValidation: true });
    setSelectedOrgId(inFlightOrganizationId);
    setSelectedScope(
      pendingWorkspaceId
        ? {
            scopeType: 'workspace',
            scopeId: pendingWorkspaceId,
          }
        : undefined
    );
    useUserContextStore.getState().setOrgId(inFlightOrganizationId);

    useContactPanelStore.reset();
    useSlidePanelStore.reset();

    applyLocationSelectionSideEffects({
      newLocationId: inFlightLocationIds[0],
      onError(errorMessage: string) {
        alerts.error({
          autoDismissAfter: 10000,
          message: errorMessage,
        });
      },
    });
  }, [
    inFlightLocationIds,
    setSelectedLocationIds,
    inFlightOrganizationId,
    setSelectedOrgId,
    setSelectedScope,
    applyLocationSelectionSideEffects,
    alerts,
    pendingWorkspaceId,
  ]);

  const {
    renderOrgInfo,
    renderLocationInfo,
    renderBannerInfo,
    onApplyError,
    onClickOrgRadio,
    handleViewLocation,
    renderPhoneRelatedModals,
  } = usePrivilegedPhoneSoftphoneIntegration({
    handleOrganizationChange,
    closeModal,
  });

  return (
    <>
      <LoadPrivilegedLocations />
      <PrivilegedLocationProvider
        accessibleLocationData={accessibleLocationData}
        accessibleGroupData={accessibleGroupData}
        getScopeName={getScopeName}
        selectedOrgId={selectedOrgId}
        selectedGroupId={selectedGroupId}
        selectedLocationIds={selectedLocationIds}
        selectedScope={selectedScope}
        scopeHierarchy={scopeHierarchy}
        closeModal={closeModal}
        renderBannerInfo={renderBannerInfo}
        renderLocationInfo={renderLocationInfo}
        renderOrgInfo={renderOrgInfo}
        onApplyError={onApplyError}
        onClickOrgRadio={onClickOrgRadio}
        onApply={handleViewLocation}
      >
        {children}
      </PrivilegedLocationProvider>
      {renderPhoneRelatedModals()}
    </>
  );
};

const LocationPickerStoreProvider = ({
  children,
  isSimpleView,
}: {
  children: React.ReactNode;
  isSimpleView?: boolean;
}) => {
  const { selectedGroupId, selectedLocationIds, selectedOrgId } = useAppScopeSelection();

  return (
    <LocationPickerProvider
      initialState={{
        organizationViewId: selectedOrgId,
        inFlightOrganizationId: selectedOrgId,
        inFlightLocationIds: selectedLocationIds,
        inFlightGroupId: selectedGroupId,
        groupViewId: selectedGroupId,
        isSimpleView: isSimpleView,
        isPrivilegedPicker: true,
      }}
    >
      {children}
    </LocationPickerProvider>
  );
};

export const PrivilegedLocationPicker = ({
  closeModal,
  isSimpleView,
}: {
  closeModal?: () => void;
  isSimpleView?: boolean;
}) => {
  return (
    <LocationPickerStoreProvider isSimpleView={isSimpleView}>
      <LocationPickerProviderPrivileged closeModal={closeModal}>
        <PrivilegedLocationPickerNew />
      </LocationPickerProviderPrivileged>
    </LocationPickerStoreProvider>
  );
};
